import { filterOperatorMap } from '@shared/consts';
import { ColumnActiveFilter, ColumnToFilter } from '@shared/interfaces/dev';

/**
 * Filters an array of items based on a set of active filters.
 *
 * @template T - The type of items in the array.
 * @param items - The array of items to be filtered.
 * @param columnsFilters - An array of column filter definitions, each specifying a field and its type.
 * @param filters - An array of active filter objects, each specifying a field, operator, and value.
 * @returns A new array containing only the items that match all active filters.
 *
 * Each filter is applied using the corresponding operator from the filterOperatorMap,
 * and only items that satisfy every filter are included in the result.
 */
export function filterItemsByActiveFilters<T extends object>(
  items: T[],
  columnsFilters: ColumnToFilter<T>[],
  filters: ColumnActiveFilter<T>[]
): T[] {
  return items.filter((item) =>
    filters.every((filter) => {
      const { type, field } = columnsFilters.find((column) => column.field === filter.field)!;

      if (!type) return false;

      if ((field as string).includes('.')) {
        const [parentKey, childKey] = (field as string).split('.') as [keyof T & string, string];
        const parentObject = item[parentKey] as Record<string, any>;

        return filterOperatorMap.get(type)![filter.operator](parentObject?.[childKey], filter.value);
      }

      return filterOperatorMap.get(type)![filter.operator](item[field as keyof T], filter.value);
    })
  );
}
